var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { ReflectUtils } from "@dooboostore/core";
import { buildSwcParameterArgs, buildCommonKindValues } from "./parameter.js";
const RESIZE_OBSERVER_METADATA_KEY = Symbol.for("simple-web-component:resize-observer");
const applyResizeObserver = (selector, options, targetObj, propertyKey) => {
  const constructor = targetObj.constructor;
  let observers = ReflectUtils.getOwnMetadata(RESIZE_OBSERVER_METADATA_KEY, constructor);
  if (!observers) {
    observers = [];
    ReflectUtils.defineMetadata(RESIZE_OBSERVER_METADATA_KEY, observers, constructor);
  }
  observers.push({ propertyKey, selector, options });
};
const resolveResizeObserverArgs = (selectorOrOptions, maybeOptions, extra) => {
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return { selector: selectorOrOptions, options: { ...maybeOptions ?? {}, ...extra } };
  }
  return { selector: "$this", options: { ...selectorOrOptions ?? {}, ...extra } };
};
const dispatchResizeObserver = (selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, extra) => {
  if ((typeof maybeOptionsOrPropertyKey === "string" || typeof maybeOptionsOrPropertyKey === "symbol") && descriptor !== void 0) {
    applyResizeObserver("$this", { ...extra }, selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey);
    return;
  }
  const { selector, options } = resolveResizeObserverArgs(
    selectorOrOptionsOrTarget,
    maybeOptionsOrPropertyKey,
    extra
  );
  return (targetObj, propertyKey) => {
    applyResizeObserver(selector, options, targetObj, propertyKey);
  };
};
function resizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, {});
}
function resizeObserverDelegateLight(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "light", delegate: true });
}
function resizeObserverDelegateShadow(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "shadow", delegate: true });
}
function resizeObserverDelegateAll(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "all", delegate: true });
}
function resizeObserverDelegate(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "auto", delegate: true });
}
function resizeObserverLight(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "light" });
}
function resizeObserverShadow(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "shadow" });
}
function resizeObserverAll(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchResizeObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "all" });
}
function getResizeObserverMetadata(target) {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(RESIZE_OBSERVER_METADATA_KEY, constructor).flat();
}
import { inObserverScope } from "../types/index.js";
import { SwcUtils } from "../utils/Utils.js";
class ResizeObserverLifeCycler {
  constructor() {
    __publicField(this, "removeObserverCallbacksMap", /* @__PURE__ */ new WeakMap());
  }
  onConnected(helperHostSet, _set, scope) {
    const inst = helperHostSet.$this;
    const currentWin = helperHostSet.$w;
    const removeObserverCallbacks = scope === "rest" && this.removeObserverCallbacksMap.get(inst) || [];
    this.removeObserverCallbacksMap.set(inst, removeObserverCallbacks);
    const allMeta = (getResizeObserverMetadata(inst) ?? []).filter((m) => inObserverScope(m, scope));
    if (allMeta.length === 0) return;
    const root = inst.shadowRoot || inst;
    for (const m of allMeta) {
      if (m.options.removeObserver) removeObserverCallbacks.push({ fn: m.options.removeObserver, target: root, opts: m.options });
    }
    const metaTargets = /* @__PURE__ */ new Map();
    const callback = (entries, obs) => {
      for (const m of allMeta) {
        let matchedEls;
        if (m.options.delegate && typeof m.selector === "string") {
          const isThis = m.selector === "$this" || m.selector === "";
          matchedEls = entries.map((e) => e.target).filter((el) => {
            if (!el || el.nodeType !== 1) return false;
            if (isThis) return true;
            return el.matches?.(m.selector) || !!el.closest?.(m.selector);
          });
        } else {
          const set = metaTargets.get(m);
          matchedEls = entries.map((e) => e.target).filter((t) => t && t.nodeType === 1 && !!set?.has(t));
        }
        if (matchedEls.length === 0) continue;
        const opts = m.options;
        void (async () => {
          const helper = SwcUtils.getHelperAndHostSet(inst);
          if (opts.filter && !await opts.filter(matchedEls, { currentThis: inst, helper })) return;
          const common = buildCommonKindValues(inst, helperHostSet.$w);
          const legacyArgs = [matchedEls, entries.filter((e) => matchedEls.includes(e.target)), obs, { ...common.hostSet, $root: root }];
          const buildArgs = (beforeReturn) => buildSwcParameterArgs(inst, m.propertyKey, {
            ...common,
            resizeObserverBeforeReturn: beforeReturn
          }, [...legacyArgs, beforeReturn]);
          let args = buildArgs(void 0);
          if (opts.before) {
            const br = await opts.before(matchedEls, { currentThis: inst, helper });
            args = buildArgs(br);
          }
          let result, error;
          try {
            result = await inst[m.propertyKey](...args);
          } catch (e) {
            error = e;
          } finally {
            if (opts.finally) await opts.finally(matchedEls, { currentThis: inst, helper }, { args, result, error });
          }
          if (error) throw error;
        })().catch((e) => console.error("[SWC] resizeObserver handler error:", e));
      }
    };
    const resizeObserverSet = [];
    for (const m of allMeta) {
      if (m.options.delegate && typeof m.selector === "string") {
        resizeObserverSet.push({ target: m.selector, delegate: true, delegateRoot: m.options.root, options: m.options.box ? { box: m.options.box } : void 0, callback });
        continue;
      }
      let targets = [];
      if (typeof m.selector === "function") {
        const res = m.selector(inst, helperHostSet);
        if (res instanceof currentWin.HTMLElement) targets = [res];
        else if (res instanceof currentWin.NodeList) targets = Array.from(res).filter((e) => e instanceof currentWin.HTMLElement);
        else if (Array.isArray(res)) targets = res.filter((e) => e instanceof currentWin.HTMLElement);
      } else if (typeof m.selector === "string") {
        if (m.selector === "$this" || m.selector === "") {
          targets = [inst];
        } else {
          const r = m.options.root || "auto";
          const scopes = [];
          if (r === "auto") scopes.push(inst.shadowRoot || inst);
          else if (r === "light") scopes.push(inst);
          else if (r === "shadow" && inst.shadowRoot) scopes.push(inst.shadowRoot);
          else if (r === "all") {
            scopes.push(inst);
            if (inst.shadowRoot) scopes.push(inst.shadowRoot);
          }
          for (const s of scopes) {
            const found = s?.querySelectorAll?.(m.selector);
            if (found?.length) targets.push(...Array.from(found));
          }
        }
      }
      metaTargets.set(m, new WeakSet(targets));
      for (const el of targets) resizeObserverSet.push({ target: el, options: m.options.box ? { box: m.options.box } : void 0, callback });
    }
    return { resizeObserverSet };
  }
  onDisconnected(helperHostSet) {
    const inst = helperHostSet.$this;
    for (const cb of this.removeObserverCallbacksMap.get(inst) ?? []) {
      try {
        cb.fn(cb.target, cb.opts);
      } catch (e) {
        console.error("[SWC] removeObserver error:", e);
      }
    }
    this.removeObserverCallbacksMap.delete(inst);
  }
}
export {
  RESIZE_OBSERVER_METADATA_KEY,
  ResizeObserverLifeCycler,
  getResizeObserverMetadata,
  resizeObserver,
  resizeObserverAll,
  resizeObserverDelegate,
  resizeObserverDelegateAll,
  resizeObserverDelegateLight,
  resizeObserverDelegateShadow,
  resizeObserverLight,
  resizeObserverShadow
};
//# sourceMappingURL=resizeObserver.js.map
